<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetContractNumbersData',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    contract: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );

  const act_7315092382398562_reloadComponentData_bwid5e0xr =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_xy16cmeps =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_bwid5e0xr,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_xy16cmeps],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_xy16cmeps,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_xy16cmeps = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_xy16cmeps.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpFormGroup
      name="contract_number"
      :label="pgettext('Contract', 'Contract number')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      required
      :description="
        pgettext(
          'Contract',
          'Any format is allowed, but the contract number must be unique.'
        )
      "
    >
      <ItpFormInput
        name="contract_number"
        v-model="contract.contract_number"
        required
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="description"
      :label="pgettext('Contract', 'Description')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
    >
      <ItpFormInput name="description" v-model="contract.extras.description">
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="reference_number"
      :label="pgettext('Contract', 'Reference number')"
      :label-cols-md="4"
      :content-cols-md="5"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="reference_number"
        v-model="contract.extras.reference_number"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="folder_number"
      :label="pgettext('Contract', 'Folder number')"
      :label-cols-md="4"
      :content-cols-md="3"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="folder_number"
        v-model="contract.extras.folder_number"
      >
      </ItpFormInput>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
